import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowLeft, Check, Link2, ShoppingBag, ShoppingCart } from "lucide-react";
import SearchBar from "../components/ordering/SearchBar";
import ItemGrid from "../components/ordering/ItemGrid";
import CartPanel from "../components/ordering/CartPanel";
import CheckoutModal from "../components/ordering/CheckoutModal";
import { CartProvider, formatMoney, useCart } from "../context/CartContext";
import useItemSearch from "../hooks/useItemSearch";
import useFavourites from "../hooks/useFavourites";
import { getStoreName } from "../utils/itemSearchApi";

// Shared ordering screen.
//   /order/:customerId   -> public page (no back button)
//   /online-ordering     -> opened from Settings (back button + share link)
function OrderScreen({ customerId, onBack, showShareLink }) {
  const [query, setQuery] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [storeName, setStoreName] = useState(null);

  // Show the store's name (from the store table) instead of its customer_id.
  useEffect(() => {
    if (!customerId) return undefined;
    const controller = new AbortController();
    getStoreName(customerId, { signal: controller.signal })
      .then((name) => setStoreName(name))
      .catch(() => setStoreName(null));
    return () => controller.abort();
  }, [customerId]);
  const { items, loading, error, retry, isTyping } = useItemSearch(customerId, query);
  // Browse-view favourites (both Inventory and AI Menu) come from
  // GET /items/browse (see ItemGrid -> useOnlineOrderingBrowse); this hook
  // is still needed for isFavourite/toggleFavourite, which drive the heart
  // button on every Inventory item card, in search results and in browse.
  const { isFavourite, toggleFavourite } = useFavourites(customerId);
  const { itemCount, totalCents } = useCart();

  // Lock page scroll while the mobile cart drawer is open.
  useEffect(() => {
    if (!cartOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setCartOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [cartOpen]);

  const showLoading = loading || (isTyping && items.length === 0);

  const copyShareLink = async () => {
    const link = `${window.location.origin}/order/${encodeURIComponent(customerId)}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      window.prompt("Copy this link to share your online ordering page:", link);
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-20 border-b border-gray-100 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:gap-6">
          <div className="flex items-center justify-between sm:flex-none">
            <div className="flex items-center gap-2">
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="-ml-1 rounded-lg p-1.5 text-gray-600 hover:bg-gray-100"
                  aria-label="Back to settings"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              )}
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white">
                <ShoppingBag className="h-5 w-5" />
              </span>
              <div className="leading-tight">
                <p className="max-w-[12rem] truncate text-base font-semibold text-gray-900 sm:max-w-xs" title={storeName || undefined}>
                  {storeName || "Our store"}
                </p>
                <p className="text-xs text-gray-500">Order online</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
            {showShareLink && (
              <button
                type="button"
                onClick={copyShareLink}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 sm:hidden"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Link2 className="h-4 w-4" />}
                {copied ? "Copied" : "Share"}
              </button>
            )}
            <button
              type="button"
              onClick={() => setCartOpen(true)}
              className="relative rounded-lg p-2 text-gray-700 hover:bg-gray-100 lg:hidden"
              aria-label={`Open cart, ${itemCount} items`}
            >
              <ShoppingCart className="h-6 w-6" />
              {itemCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-blue-600 px-1 text-[11px] font-semibold text-white">
                  {itemCount}
                </span>
              )}
            </button>
            </div>
          </div>
          <div className="flex-1">
            <SearchBar value={query} onChange={setQuery} loading={loading} />
          </div>
          {showShareLink && (
            <button
              type="button"
              onClick={copyShareLink}
              className="hidden flex-none items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:inline-flex"
            >
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Link2 className="h-4 w-4" />}
              {copied ? "Link copied" : "Share link"}
            </button>
          )}
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-6 px-4 py-6">
        <main className="min-w-0 flex-1 pb-24 lg:pb-0">
          <ItemGrid
            query={query}
            items={items}
            loading={showLoading}
            error={error}
            onRetry={retry}
            isFavourite={isFavourite}
            onToggleFavourite={toggleFavourite}
            customerId={customerId}
          />
        </main>

        {/* Desktop: sticky cart column */}
        <aside className="hidden w-96 flex-none lg:block">
          <div className="sticky top-24 h-[calc(100vh-7.5rem)] overflow-hidden rounded-xl border border-gray-100 shadow-sm">
            <CartPanel onCheckout={() => setCheckoutOpen(true)} />
          </div>
        </aside>
      </div>

      {/* Mobile: floating cart summary */}
      {itemCount > 0 && !cartOpen && (
        <div className="fixed inset-x-0 bottom-0 z-20 p-4 lg:hidden">
          <button
            type="button"
            onClick={() => setCartOpen(true)}
            className="flex w-full items-center justify-between rounded-xl bg-blue-600 px-5 py-3.5 text-white shadow-lg"
          >
            <span className="flex items-center gap-2 text-sm font-semibold">
              <ShoppingCart className="h-5 w-5" /> View cart ({itemCount})
            </span>
            <span className="text-sm font-semibold tabular-nums">{formatMoney(totalCents)}</span>
          </button>
        </div>
      )}

      {/* Mobile: cart drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-30 lg:hidden" role="dialog" aria-modal="true" aria-label="Cart">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCartOpen(false)} />
          <div className="absolute inset-y-0 right-0 w-full max-w-md shadow-xl">
            <CartPanel
              onClose={() => setCartOpen(false)}
              onCheckout={() => {
                setCartOpen(false);
                setCheckoutOpen(true);
              }}
            />
          </div>
        </div>
      )}

      {checkoutOpen && <CheckoutModal customerId={customerId} onClose={() => setCheckoutOpen(false)} />}
    </div>
  );
}

export function OnlineOrderView({ customerId, onBack, showShareLink = false }) {
  return (
    <CartProvider customerId={customerId}>
      <OrderScreen customerId={customerId} onBack={onBack} showShareLink={showShareLink} />
    </CartProvider>
  );
}

export default function OnlineOrderPage() {
  const { customerId } = useParams();
  return <OnlineOrderView customerId={customerId} />;
}
